import { useState, useEffect, useRef } from "react";
import { supabase } from "./lib/supabase";

// ============================================================
// WEDYRA — Chat / Messagerie couple ↔ prestataire
// ============================================================

export default function Chat({ user, C, conversations, onUnreadChange }) {
  const [convos, setConvos] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const msgEndRef = useRef(null);
  const pollRef = useRef(null);

  const isCouple = user.role === "couple";

  // ─── Charger les conversations ───
  useEffect(() => {
    loadConversations();
    // Poll toutes les 8 secondes
    pollRef.current = setInterval(() => {
      loadConversations();
      if (activeConv) loadMessages(activeConv.key);
    }, 8000);
    return () => clearInterval(pollRef.current);
  }, [user.id]);

  const loadConversations = async () => {
    if (!user.id) {
      setConvos(conversations || []);
      setLoading(false);
      return;
    }

    try {
      // Récupérer les demandes de contact acceptées ou envoyées
      let query;
      if (isCouple) {
        query = supabase.from("demandes_contact")
          .select("*, prestataires(id, nom_entreprise, ville, email, description)")
          .eq("couple_id", user.id)
          .in("statut", ["envoyee", "acceptee"])
          .order("created_at", { ascending: false });
      } else {
        query = supabase.from("demandes_contact")
          .select("*, couples(id, prenom1, prenom2, email, ville, date_mariage)")
          .eq("prestataire_id", user.id)
          .in("statut", ["envoyee", "acceptee"])
          .order("created_at", { ascending: false });
      }

      const { data } = await query;
      if (data) {
        const convList = data.map(d => {
          const otherName = isCouple
            ? d.prestataires?.nom_entreprise || "Prestataire"
            : `${d.couples?.prenom1 || ""} & ${d.couples?.prenom2 || ""}`;
          const otherId = isCouple ? d.prestataire_id : d.couple_id;
          const key = `${d.couple_id}_${d.prestataire_id}`;
          const otherInfo = isCouple
            ? { ville: d.prestataires?.ville, desc: d.prestataires?.description }
            : { ville: d.couples?.ville, date: d.couples?.date_mariage };
          return { key, otherName, otherId, statut: d.statut, ...otherInfo };
        });
        setConvos(convList);

        // Compter les non-lus par conversation
        const { data: unread } = await supabase
          .from("chat_messages")
          .select("conversation_key")
          .eq("is_read", false)
          .neq("sender_id", user.id);

        if (unread) {
          const counts = {};
          unread.forEach(m => { counts[m.conversation_key] = (counts[m.conversation_key] || 0) + 1; });
          setUnreadCounts(counts);
        }
      }
    } catch (e) {
      console.error("Chat load error:", e);
    }
    setLoading(false);
  };

  // ─── Charger les messages d'une conversation ───
  const loadMessages = async (convKey) => {
    if (!convKey) return;
    try {
      const { data } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_key", convKey)
        .order("created_at", { ascending: true });

      if (data) {
        setMessages(data);
        // Marquer comme lu
        await supabase
          .from("chat_messages")
          .update({ is_read: true })
          .eq("conversation_key", convKey)
          .neq("sender_id", user.id)
          .eq("is_read", false);

        setUnreadCounts(prev => ({ ...prev, [convKey]: 0 }));
      }
    } catch (e) {
      console.error("Messages load error:", e);
    }
  };

  // ─── Ouvrir une conversation ───
  const openConversation = (conv) => {
    setActiveConv(conv);
    loadMessages(conv.key);
  };

  // ─── Envoyer un message ───
  const sendMessage = async () => {
    if (!newMsg.trim() || !activeConv || sending) return;
    setSending(true);
    const msg = {
      conversation_key: activeConv.key,
      sender_type: isCouple ? "couple" : "pro",
      sender_id: user.id || "demo",
      sender_name: user.nom,
      content: newMsg.trim(),
    };

    try {
      if (user.id) {
        await supabase.from("chat_messages").insert(msg);
      }
      setMessages(prev => [...prev, { ...msg, id: Date.now(), created_at: new Date().toISOString() }]);
      setNewMsg("");
    } catch (e) {
      console.error("Send error:", e);
    }
    setSending(false);
  };

  // Auto-scroll
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const card = { background: C.card, borderRadius: 18, border: `1px solid ${C.cardBorder}` };
  const totalUnread = Object.values(unreadCounts).reduce((s, n) => s + n, 0);

  useEffect(() => { onUnreadChange?.(totalUnread); }, [totalUnread]);

  // ─── PAS DE CONVERSATIONS ───
  if (!loading && convos.length === 0) {
    return (
      <div style={{ ...card, textAlign: "center", padding: "60px 40px" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: C.text, marginBottom: 10 }}>
          Aucune conversation
        </div>
        <p style={{ fontSize: 14, color: C.textLight, maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>
          {isCouple
            ? "Contactez des prestataires depuis l'onglet ✦ Matching pour démarrer une conversation."
            : "Vous recevrez des messages quand des couples vous contacteront."}
        </p>
      </div>
    );
  }

  // ─── LAYOUT CHAT ───
  return (
    <div style={{ display: "flex", gap: 0, height: "calc(100vh - 280px)", minHeight: 450, ...card, overflow: "hidden" }}>

      {/* ─── LISTE CONVERSATIONS ─── */}
      <div style={{ width: 280, borderRight: `1px solid ${C.cardBorder}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.cardBorder}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.text }}>Messages</div>
            {totalUnread > 0 && (
              <span style={{ width: 22, height: 22, borderRadius: "50%", background: C.primary, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{totalUnread}</span>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: C.textLight, fontSize: 13 }}>Chargement...</div>
          ) : (
            convos.map(conv => {
              const isActive = activeConv?.key === conv.key;
              const unread = unreadCounts[conv.key] || 0;
              return (
                <div key={conv.key} onClick={() => openConversation(conv)}
                  style={{
                    padding: "14px 18px", cursor: "pointer", transition: "all 0.15s",
                    background: isActive ? C.primaryBg : "transparent",
                    borderBottom: `1px solid ${C.cardBorder}`,
                    borderLeft: isActive ? `3px solid ${C.primary}` : "3px solid transparent",
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = C.rowHover; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: unread > 0 ? 700 : 500, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {isCouple ? "🏢" : "💒"} {conv.otherName}
                      </div>
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }}>
                        {conv.ville && `📍 ${conv.ville}`}
                        {conv.statut === "acceptee" && <span style={{ color: C.green, marginLeft: 6 }}>✓ Accepté</span>}
                        {conv.statut === "envoyee" && <span style={{ color: C.amber, marginLeft: 6 }}>En attente</span>}
                      </div>
                    </div>
                    {unread > 0 && (
                      <span style={{ width: 20, height: 20, borderRadius: "50%", background: C.primary, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 8 }}>{unread}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ─── ZONE DE CHAT ─── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {!activeConv ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: C.textLight }}>
            <div style={{ fontSize: 40 }}>💬</div>
            <p style={{ fontSize: 14 }}>Sélectionnez une conversation</p>
          </div>
        ) : (
          <>
            {/* Header conversation */}
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, color: C.text }}>
                  {isCouple ? "🏢" : "💒"} {activeConv.otherName}
                </div>
                <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
                  {activeConv.ville && `📍 ${activeConv.ville}`}
                  {activeConv.desc && ` · ${activeConv.desc.substring(0, 50)}...`}
                  {activeConv.date && ` · 💒 ${new Date(activeConv.date).toLocaleDateString("fr-FR")}`}
                </div>
              </div>
              <span style={{
                fontSize: 11, padding: "4px 12px", borderRadius: 100, fontWeight: 600,
                background: activeConv.statut === "acceptee" ? C.greenBg : C.amberBg,
                color: activeConv.statut === "acceptee" ? C.green : C.amber,
              }}>
                {activeConv.statut === "acceptee" ? "✓ Connecté" : "⏳ En attente"}
              </span>
              {activeConv.telephone && <a href={`tel:${activeConv.telephone}`} style={{width:36,height:36,borderRadius:10,background:C.greenBg,border:`1px solid ${C.green}`,display:"flex",alignItems:"center",justifyContent:"center",textDecoration:"none",fontSize:16,flexShrink:0}}>📞</a>}
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
              {messages.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: C.textLight }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
                  <p style={{ fontSize: 13 }}>Démarrez la conversation !</p>
                  <p style={{ fontSize: 12, marginTop: 6, color: C.textMuted }}>
                    {isCouple ? "Présentez votre projet de mariage et posez vos questions." : "Répondez au couple et présentez vos services."}
                  </p>
                </div>
              )}

              {messages.map((msg, i) => {
                const isMine = msg.sender_id === (user.id || "demo");
                const showDate = i === 0 || new Date(msg.created_at).toDateString() !== new Date(messages[i - 1]?.created_at).toDateString();
                return (
                  <div key={msg.id || i}>
                    {showDate && (
                      <div style={{ textAlign: "center", margin: "12px 0 8px", fontSize: 11, color: C.textMuted }}>
                        {new Date(msg.created_at).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start" }}>
                      <div style={{
                        maxWidth: "75%", padding: "10px 16px", borderRadius: 16,
                        borderBottomRightRadius: isMine ? 4 : 16,
                        borderBottomLeftRadius: isMine ? 16 : 4,
                        background: isMine ? C.primary : C.primaryBg,
                        color: isMine ? "#fff" : C.text,
                      }}>
                        {!isMine && <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: C.primary }}>{msg.sender_name}</div>}
                        <div style={{ fontSize: 14, lineHeight: 1.5 }}>{msg.content}</div>
                        <div style={{ fontSize: 10, marginTop: 4, textAlign: "right", opacity: 0.6 }}>
                          {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.cardBorder}`, display: "flex", gap: 10, alignItems: "center" }}>
              <input
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Écrivez votre message..."
                style={{
                  flex: 1, padding: "12px 16px", borderRadius: 14,
                  border: `1.5px solid ${C.cardBorder}`, background: C.card,
                  color: C.text, fontFamily: "'DM Sans',sans-serif", fontSize: 14,
                  outline: "none", boxSizing: "border-box",
                }}
                onFocus={e => e.target.style.borderColor = C.primary}
                onBlur={e => e.target.style.borderColor = C.cardBorder}
              />
              <button onClick={sendMessage} disabled={!newMsg.trim() || sending}
                style={{
                  width: 44, height: 44, borderRadius: 14, border: "none",
                  background: newMsg.trim() ? C.primary : C.cardBorder,
                  color: "#fff", fontSize: 18, cursor: newMsg.trim() ? "pointer" : "default",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.2s", flexShrink: 0,
                }}>
                {sending ? "⏳" : "➤"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
